import {
  createContext,
  FC,
  useEffect,
  useMemo,
} from 'react';
import { useUserSettings } from 'src/hooks/useSettings';

/* ------------------------------------------------------------------ */

export interface ISettingsContext {
  settings?: any;
  setSetting?: (key: string | string[], value: any) => void;
  setMultipleSettings?: (
    patches:
      | { key: string | string[]; value?: any; delete?: boolean }
      | { key: string | string[]; value?: any; delete?: boolean }[]
  ) => void;
  deleteSetting?: (key: string | string[]) => void;

  /** NEW: indicates there's a real base (server or cached) to write against */
  settingsReady?: boolean;
}
export const DEFAULT_INPUT_LANGUAGE = 2; 
const defaultSettings = {
  Quicklinks: ['Products'],
  DevelopmentMenu: false,
  AlertMessages: false,
  InputLanguage: DEFAULT_INPUT_LANGUAGE,
  DisplayLanguage: 1,
  UseFileCache: false,
};

export const SettingsContext = createContext<ISettingsContext>({});

/* ------------------------------------------------------------------ */

const STORAGE_KEY = 'app.userSettings';

export const SettingsProvider: FC<any> = ({ children }) => {
  const [
    userSettingsObject,
    updateSetting,
    updateMultipleSettings,
    deleteSetting,
  ] = useUserSettings();

  // Safe read from localStorage
  const localSettings = useMemo(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const hasRealSettings =
    !!userSettingsObject && Object.keys(userSettingsObject).length > 0;

  // 🔑 ALWAYS start from defaultSettings, then layer on top:
  // 1) localStorage snapshot
  // 2) server/user settings (if any)
  const settings = useMemo(() => {
    const base: any = { ...defaultSettings };

    if (localSettings && Object.keys(localSettings).length > 0) {
      Object.assign(base, localSettings);
    }

    if (hasRealSettings) {
      Object.assign(base, userSettingsObject);
    }

    return base;
  }, [localSettings, hasRealSettings, userSettingsObject]);

  // Ready if we have non-empty server settings OR a cached copy (not just defaults)
  const settingsReady = hasRealSettings || !!localSettings;

  // Cache the merged settings (including defaults) to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (e) {
      console.error('Failed to cache user settings:', e);
    }
  }, [settings]);

  return (
    <SettingsContext.Provider
      value={{
        settings,
        setSetting: updateSetting,
        setMultipleSettings: updateMultipleSettings,
        deleteSetting,
        settingsReady,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};
